/** Standardized stale times for TanStack Query */
export const STALE_TIMES = {
  /** 30s — frequently changing data (notifications, real-time) */
  SHORT: 30_000,
  /** 1m — default (already set in query-client) */
  DEFAULT: 60_000,
  /** 5m — moderately changing data (filter options, stats) */
  MEDIUM: 5 * 60_000,
  /** 30m — relatively stable reference data (locations, companies) */
  LONG: 30 * 60_000,
} as const;

/** Query key factories for consistent cache key structure */
export const queryKeys = {
  notifications: {
    all: ['notifications'] as const,
    unreadCount: (orgPid: string) => ['notifications', orgPid, 'unread-count'] as const,
    recent: (orgPid: string) => ['notifications', orgPid, 'recent'] as const,
    list: (orgPid: string, page: number, type?: string) =>
      ['notifications', orgPid, page, type] as const,
  },
  marketData: {
    all: ['market-data'] as const,
    locationHierarchy: (orgPid: string) => ['market-data', 'location-hierarchy', orgPid] as const,
    locationHierarchyRegions: () => ['market-data', 'location-hierarchy-regions'] as const,
    charterCompanies: (
      orgPid: string,
      filters?: { sailingAreaPids?: string[]; countryPids?: string[]; basePids?: string[] }
    ) => ['market-data', 'charter-companies', orgPid, filters] as const,
  },
  market: {
    browse: (orgPid: string, params: Record<string, unknown>) =>
      ['market-browse', orgPid, params] as const,
    browseAll: ['market-browse'] as const,
    tracked: (orgPid: string, params?: Record<string, unknown>) =>
      ['market-tracked', orgPid, params] as const,
    trackedAll: (orgPid: string) => ['market-tracked', orgPid] as const,
    stats: (orgPid: string) => ['market-stats', orgPid] as const,
    filterOptions: (orgPid: string) => ['market-filter-options', orgPid] as const,
    models: (orgPid: string, kind: string) => ['market-models', orgPid, kind] as const,
    boat: (boatId: string, detail: string) => ['market-boat', boatId, detail] as const,
    boatsList: (orgPid: string, tag: string) => ['market-boats', orgPid, tag] as const,
  },
  compSets: {
    all: ['comp-sets'] as const,
    list: (orgPid: string) => ['comp-sets', orgPid] as const,
    listTagged: (orgPid: string, tag: string) => ['comp-sets', orgPid, tag] as const,
    detail: (pid: string) => ['comp-set', pid] as const,
    detailAll: ['comp-set'] as const,
    calendar: (pid: string, year: number, boatPid?: string) =>
      ['comp-set', pid, 'calendar', year, boatPid] as const,
    weeklyComparison: (id: string, weekStart: string, source: 'mmk' | 'boataround' = 'mmk') =>
      ['comp-set', id, 'weekly-comparison', weekStart, source] as const,
    weeklyComparisonAll: (id: string) => ['comp-set', id, 'weekly-comparison'] as const,
    weekRank: (id: string, weekStart: string) => ['comp-set', id, 'week-rank', weekStart] as const,
    sideBySide: (id: string, year: number) => ['comp-set', id, 'side-by-side', year] as const,
    marketStatus: (orgPid: string) => ['comp-sets', orgPid, 'market-status'] as const,
  },
  alerts: {
    all: ['alert-rules'] as const,
    list: (orgPid: string, page?: number, scope?: string, ruleType?: string) =>
      ['alert-rules', orgPid, page, scope, ruleType] as const,
    byOrg: (orgPid: string) => ['alert-rules', orgPid] as const,
  },
  boats: {
    all: ['boats'] as const,
    list: (orgPid: string, tag: string) => ['boats', orgPid, tag] as const,
    pricing: (boatId: string) => ['boats', boatId, 'pricing'] as const,
  },
  analytics: {
    all: ['analytics'] as const,
    revenue: (orgPid: string, year: string) => ['analytics', 'revenue', orgPid, year] as const,
    revenueByBoat: (orgPid: string, year: string) =>
      ['analytics', 'revenue-by-boat', orgPid, year] as const,
    revenueByMonth: (orgPid: string, year: string) =>
      ['analytics', 'revenue-by-month', orgPid, year] as const,
    bookingPace: (orgPid: string, year: string) =>
      ['analytics', 'booking-pace', orgPid, year] as const,
    occupancySummary: (orgPid: string, year: string, boat?: string) =>
      ['analytics', 'occupancy-summary', orgPid, year, boat] as const,
    occupancyByMonth: (orgPid: string, year: string, boat?: string) =>
      ['analytics', 'occupancy-by-month', orgPid, year, boat] as const,
    occupancySeason: (
      orgPid: string,
      year: string,
      boat?: string,
      boatPids?: string,
      ownerPid?: string
    ) => ['analytics', 'occupancy-season', orgPid, year, boat, boatPids, ownerPid] as const,
    fleetCurrentStatus: (orgPid: string) => ['analytics', 'fleet-current-status', orgPid] as const,
    availabilityGaps: (orgPid: string) => ['analytics', 'availability-gaps', orgPid] as const,
    competitorPosition: (orgPid: string, year: string, boat?: string, compSet?: string) =>
      ['analytics', 'competitor-position', orgPid, year, boat, compSet] as const,
    competitorByMonth: (orgPid: string, year: string, boat?: string, compSet?: string) =>
      ['analytics', 'competitor-by-month', orgPid, year, boat, compSet] as const,
    seasonOverview: (orgPid: string, year: string) =>
      ['analytics', 'season-overview', orgPid, year] as const,
    competitorCompSets: (orgPid: string, year: string) =>
      ['analytics', 'competitor-comp-sets', orgPid, year] as const,
    income: (orgPid: string, year: string, boatPid: string) =>
      ['analytics', 'income', orgPid, year, boatPid] as const,
    incomeAll: (orgPid: string) => ['analytics', 'income', orgPid] as const,
    incomeYears: (orgPid: string) => ['analytics', 'income-years', orgPid] as const,
    availableYears: (orgPid: string) => ['analytics', 'available-years', orgPid] as const,
  },
  revenueGoals: {
    all: (orgPid: string) => ['revenue-goals', orgPid] as const,
    list: (orgPid: string, year: number) => ['revenue-goals', orgPid, year] as const,
  },
  owners: {
    list: (orgPid: string) => ['owners', orgPid] as const,
    options: (orgPid: string) => ['owners', orgPid, 'options'] as const,
  },
  ownerStatus: {
    detail: (token: string, year: string) => ['owner-status', token, year] as const,
  },
  strategies: {
    all: ['strategies'] as const,
    list: (orgPid: string) => ['strategies', orgPid] as const,
  },
  suggestions: {
    all: ['suggestions'] as const,
    list: (orgPid: string, page: number, search: string, status: string) =>
      ['suggestions', orgPid, page, search, status] as const,
    pending: (orgPid: string) => ['suggestions', 'pending', orgPid] as const,
  },
  integrations: {
    all: ['integrations'] as const,
    list: (orgPid: string) => ['integrations', orgPid] as const,
    detail: (orgPid: string, id: string) => ['integrations', orgPid, id] as const,
  },
  members: {
    all: ['members'] as const,
    list: (orgPid: string) => ['members', orgPid] as const,
  },
  invitations: {
    all: ['invitations'] as const,
    list: (orgPid: string) => ['invitations', orgPid] as const,
  },
  localEvents: {
    all: ['local-events'] as const,
    list: (orgPid: string) => ['local-events', orgPid] as const,
  },
  users: {
    list: (orgPid: string, params: Record<string, unknown>) => ['users', orgPid, params] as const,
  },
  dashboard: {
    layout: (orgPid: string) => ['dashboard', 'layout', orgPid] as const,
  },
  setup: {
    status: (orgPid: string) => ['setup', 'status', orgPid] as const,
  },
  admin: {
    all: ['admin'] as const,
    dashboard: () => ['admin', 'dashboard'] as const,
    companies: (params: Record<string, unknown>) => ['admin', 'companies', params] as const,
    companySearch: (q: string) => ['admin', 'company-search', q] as const,
    companyDetail: (pid: string) => ['admin', 'company', pid] as const,
    companyDeletionPreview: (pid: string) => ['admin', 'company', pid, 'deletion-preview'] as const,
    users: (params: Record<string, unknown>) => ['admin', 'users', params] as const,
    jobs: () => ['admin', 'jobs'] as const,
    charters: (params: Record<string, unknown>) => ['admin', 'charters', params] as const,
    charterCountries: () => ['admin', 'charter-countries'] as const,
  },
} as const;
